import * as THREE from 'three';
import { PIECE_COLORS, PIECE_NAMES } from '../logic/types';
import type { BlockerKind, PieceType, SpecialKind } from '../logic/types';

/**
 * Lumenwild art language
 * ----------------------
 *
 * The board is a small stage: x/z are the face of a cell and y is the lift
 * above it.  The factory deliberately keeps all geometry in a shared cache.
 * A piece returned from this module is therefore cheap to clone, safe to
 * animate independently, and does not need to own a material or a geometry.
 */

export type ArtQuality = 'high' | 'balanced' | 'mobile';
export type ChapterId = 0 | 1 | 2 | 3 | 4;
export type ConveyorDirection = 'up' | 'down' | 'left' | 'right';

export interface ArtFactoryOptions {
  quality?: ArtQuality;
  cellSize?: number;
  patternCues?: boolean;
  highContrast?: boolean;
  reducedMotion?: boolean;
  shadows?: boolean;
}

export interface PieceVisualOptions {
  scale?: number;
  selected?: boolean;
  muted?: boolean;
}

export interface BlockerVisualOptions {
  scale?: number;
  layers?: number;
  muted?: boolean;
}

export interface BoardCellOptions {
  chapter?: number;
  active?: boolean;
  locked?: boolean;
  hazard?: number;
  portal?: string;
  conveyor?: ConveyorDirection;
  spawn?: boolean;
  exit?: boolean;
  scale?: number;
}

export interface BoardFrameOptions {
  width: number;
  height: number;
  chapter?: number;
  cellSize?: number;
  scale?: number;
}

export interface ChapterAccentOptions {
  width?: number;
  height?: number;
  cellSize?: number;
  density?: number;
  seed?: number;
  scale?: number;
}

export interface PieceArtDescriptor {
  name: string;
  color: string;
  accent: string;
  icon: string;
  pattern: string;
  silhouette: string;
}

export interface SpecialArtDescriptor {
  name: string;
  cue: string;
  effect: string;
}

export interface BlockerArtDescriptor {
  name: string;
  cue: string;
  damageStates: number;
}

export interface ChapterArtDescriptor {
  name: string;
  shortName: string;
  primary: string;
  secondary: string;
  glow: string;
  background: string;
  motif: string;
}

/**
 * These descriptors are intentionally public.  UI and tutorial code can use
 * the same non-colour cues as the 3D renderer for colour-blind and compact
 * layouts.
 */
export const LUMENWILD_PIECE_ART: Readonly<Record<PieceType, PieceArtDescriptor>> = {
  0: {
    name: PIECE_NAMES[0],
    color: PIECE_COLORS[0],
    accent: '#ffd09a',
    icon: 'three-point leaf',
    pattern: 'radiating vein',
    silhouette: 'folded flame leaf',
  },
  1: {
    name: PIECE_NAMES[1],
    color: PIECE_COLORS[1],
    accent: '#e8fbff',
    icon: 'moon spot',
    pattern: 'three pale dots',
    silhouette: 'mushroom cap',
  },
  2: {
    name: PIECE_NAMES[2],
    color: PIECE_COLORS[2],
    accent: '#fff5ba',
    icon: 'toothed wheel',
    pattern: 'hexagonal hub',
    silhouette: 'flower gear',
  },
  3: {
    name: PIECE_NAMES[3],
    color: PIECE_COLORS[3],
    accent: '#f5e9ff',
    icon: 'faceted pear',
    pattern: 'three facets',
    silhouette: 'tapered prism fruit',
  },
  4: {
    name: PIECE_NAMES[4],
    color: PIECE_COLORS[4],
    accent: '#fff0d6',
    icon: 'coral quill',
    pattern: 'fan of barbs',
    silhouette: 'curled coral plume',
  },
  5: {
    name: PIECE_NAMES[5],
    color: PIECE_COLORS[5],
    accent: '#dce6ff',
    icon: 'ink bubbles',
    pattern: 'paired bubbles',
    silhouette: 'teardrop blot',
  },
  6: {
    name: PIECE_NAMES[6],
    color: PIECE_COLORS[6],
    accent: '#fff2b2',
    icon: 'sun coil',
    pattern: 'spiral and rays',
    silhouette: 'coiled sunseed',
  },
};

export const LUMENWILD_SPECIAL_ART: Readonly<Record<SpecialKind, SpecialArtDescriptor>> = {
  none: { name: 'ordinary seed', cue: 'no crest', effect: 'matches normally' },
  beamH: { name: 'horizon filament', cue: 'double horizontal arrow', effect: 'clears its row' },
  beamV: { name: 'vertical filament', cue: 'double vertical arrow', effect: 'clears its column' },
  nova: { name: 'nova bloom', cue: 'four-petal star', effect: 'bursts in a radius' },
  prism: { name: 'prism seed', cue: 'faceted orbit', effect: 'converts a colour family' },
  burst: { name: 'orbit knot', cue: 'crossed orbit bands', effect: 'detonates in chained rings' },
};

export const LUMENWILD_BLOCKER_ART: Readonly<Record<BlockerKind, BlockerArtDescriptor>> = {
  none: { name: 'clear cell', cue: 'open', damageStates: 1 },
  glass: { name: 'moon glass', cue: 'transparent pane and cracks', damageStates: 4 },
  bramble: { name: 'thorn snare', cue: 'looped vines and thorns', damageStates: 4 },
  wax: { name: 'amber wax', cue: 'faceted seal and drips', damageStates: 4 },
  lock: { name: 'seed lock', cue: 'shackle and keyhole', damageStates: 4 },
  meteor: { name: 'ember meteor', cue: 'rock and glowing fissures', damageStates: 4 },
};

export const LUMENWILD_CHAPTER_ART: readonly ChapterArtDescriptor[] = [
  {
    name: 'Emberroot Conservatory',
    shortName: 'Emberroot',
    primary: '#273857',
    secondary: '#e96f64',
    glow: '#ffbe78',
    background: '#111b35',
    motif: 'warm leaves and fireflies',
  },
  {
    name: 'Moonmoss Archive',
    shortName: 'Moonmoss',
    primary: '#263d52',
    secondary: '#78cfe0',
    glow: '#c9f6ff',
    background: '#101e38',
    motif: 'mushrooms and moon rings',
  },
  {
    name: 'Clockpetal Foundry',
    shortName: 'Clockpetal',
    primary: '#3a354b',
    secondary: '#efbe63',
    glow: '#ffe6a4',
    background: '#1b2037',
    motif: 'quiet gears and brass pollen',
  },
  {
    name: 'Resonance Reef',
    shortName: 'Reef',
    primary: '#223b53',
    secondary: '#ef9675',
    glow: '#b8fff1',
    background: '#102a40',
    motif: 'coral fans and drifting pearls',
  },
  {
    name: 'Astral Canopy',
    shortName: 'Astral',
    primary: '#302d59',
    secondary: '#aa8bea',
    glow: '#e8dcff',
    background: '#161735',
    motif: 'sun coils and quiet constellations',
  },
];

const DETAIL_BY_QUALITY: Record<ArtQuality, number> = {
  high: 18,
  balanced: 12,
  mobile: 8,
};

const PIECE_TYPES: readonly PieceType[] = [0, 1, 2, 3, 4, 5, 6];
const SPECIAL_KINDS: readonly SpecialKind[] = ['none', 'beamH', 'beamV', 'nova', 'prism', 'burst'];
const BLOCKER_KINDS: readonly BlockerKind[] = ['none', 'glass', 'bramble', 'wax', 'lock', 'meteor'];

type ShapeBuilder = (shape: THREE.Shape) => void;

interface MaterialOptions {
  roughness?: number;
  metalness?: number;
  emissive?: THREE.ColorRepresentation;
  emissiveIntensity?: number;
  transparent?: boolean;
  opacity?: number;
  side?: THREE.Side;
  depthWrite?: boolean;
  flatShading?: boolean;
}

interface PieceMaterials {
  body: THREE.MeshStandardMaterial;
  edge: THREE.MeshStandardMaterial;
  dark: THREE.MeshStandardMaterial;
  light: THREE.MeshStandardMaterial;
  glow: THREE.MeshStandardMaterial;
  metal: THREE.MeshStandardMaterial;
}

interface NormalizedCellOptions {
  chapter: number;
  active: boolean;
  locked: boolean;
  hazard: number;
  portal?: string;
  conveyor?: ConveyorDirection;
  spawn: boolean;
  exit: boolean;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function integer(value: number, fallback: number): number {
  return Number.isFinite(value) ? Math.round(value) : fallback;
}

function normalizedType(type: PieceType): PieceType {
  const value = integer(Number(type), 0);
  return (value >= 0 && value <= 6 ? value : 0) as PieceType;
}

function normalizedSpecial(special: SpecialKind | undefined): SpecialKind {
  return special && SPECIAL_KINDS.includes(special) ? special : 'none';
}

function normalizedBlocker(kind: BlockerKind): BlockerKind {
  return BLOCKER_KINDS.includes(kind) ? kind : 'none';
}

function chapterIndex(chapter: number | undefined): ChapterId {
  const value = integer(chapter ?? 0, 0);
  return (clamp(value, 0, LUMENWILD_CHAPTER_ART.length - 1) as ChapterId);
}

function brighten(color: THREE.ColorRepresentation, lightness: number, saturation = 0): string {
  const value = new THREE.Color(color);
  value.offsetHSL(0, saturation, lightness);
  return `#${value.getHexString()}`;
}

function seededRandom(seed: number): () => number {
  let state = (seed | 0) ^ 0x9e3779b9;
  return () => {
    state = Math.imul(state ^ (state >>> 16), 0x21f0aaad);
    state = Math.imul(state ^ (state >>> 15), 0x735a2d97);
    state ^= state >>> 15;
    return (state >>> 0) / 4294967296;
  };
}

function hashString(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash | 0;
}

export class ArtFactory {
  readonly options: Required<ArtFactoryOptions>;

  private readonly detail: number;
  private readonly radialSegments: number;
  private readonly geometries = new Map<string, THREE.BufferGeometry>();
  private readonly materials = new Map<string, THREE.Material>();
  private readonly piecePrototypes = new Map<string, THREE.Group>();
  private readonly blockerPrototypes = new Map<string, THREE.Group>();
  private readonly cellPrototypes = new Map<string, THREE.Group>();
  private readonly accentPrototypes = new Map<string, THREE.Group>();

  constructor(options: ArtFactoryOptions = {}) {
    const quality = options.quality ?? 'balanced';
    this.options = {
      quality,
      cellSize: Math.max(0.4, options.cellSize ?? 1),
      patternCues: options.patternCues ?? true,
      highContrast: options.highContrast ?? false,
      reducedMotion: options.reducedMotion ?? false,
      shadows: options.shadows ?? true,
    };
    this.detail = DETAIL_BY_QUALITY[quality];
    this.radialSegments = Math.max(6, Math.floor(this.detail * 0.75));
  }

  /** Build a normal or special piece from shared, immutable prototypes. */
  createPiece(type: PieceType, special: SpecialKind = 'none', options: PieceVisualOptions = {}): THREE.Group {
    const safeType = normalizedType(type);
    const safeSpecial = normalizedSpecial(special);
    const key = `${safeType}:${safeSpecial}`;
    let prototype = this.piecePrototypes.get(key);
    if (!prototype) {
      prototype = this.buildPiecePrototype(safeType, safeSpecial);
      this.piecePrototypes.set(key, prototype);
    }

    const instance = prototype.clone(true);
    instance.name = `LumenwildPiece:${PIECE_NAMES[safeType]}:${safeSpecial}`;
    instance.userData = { ...prototype.userData, instance: true };
    const scale = options.scale ?? 1;
    instance.scale.setScalar(scale);
    if (options.muted) instance.userData.muted = true;
    if (options.selected) instance.add(this.buildSelectionHalo(safeType));
    return instance;
  }

  /** Explicit alias for controllers that keep special creation separate. */
  createSpecialPiece(type: PieceType, special: SpecialKind, options: PieceVisualOptions = {}): THREE.Group {
    return this.createPiece(type, special, options);
  }

  /** Build one of the six blocker families, with 0..3 meaning damage state. */
  createBlocker(kind: BlockerKind, damageState = 0, options: BlockerVisualOptions = {}): THREE.Group {
    const safeKind = normalizedBlocker(kind);
    const stage = clamp(integer(damageState, 0), 0, 3);
    const key = `${safeKind}:${stage}:${options.layers ?? ''}`;
    let prototype = this.blockerPrototypes.get(key);
    if (!prototype) {
      prototype = this.buildBlockerPrototype(safeKind, stage, options.layers);
      this.blockerPrototypes.set(key, prototype);
    }

    const instance = prototype.clone(true);
    instance.name = `LumenwildBlocker:${safeKind}:damage-${stage}`;
    instance.userData = { ...prototype.userData, instance: true, damageState: stage };
    instance.scale.setScalar(options.scale ?? 1);
    if (options.muted) instance.userData.muted = true;
    return instance;
  }

  /** Build a cell floor and its readable mechanic overlays. */
  createBoardCell(options: BoardCellOptions = {}): THREE.Group {
    const normalized: NormalizedCellOptions = {
      chapter: chapterIndex(options.chapter),
      active: options.active ?? true,
      locked: options.locked ?? false,
      hazard: clamp(integer(options.hazard ?? 0, 0), 0, 3),
      portal: options.portal,
      conveyor: options.conveyor,
      spawn: options.spawn ?? false,
      exit: options.exit ?? false,
    };
    const key = [
      normalized.chapter,
      normalized.active ? 1 : 0,
      normalized.locked ? 1 : 0,
      normalized.hazard,
      normalized.portal ?? '',
      normalized.conveyor ?? '',
      normalized.spawn ? 1 : 0,
      normalized.exit ? 1 : 0,
    ].join('|');
    let prototype = this.cellPrototypes.get(key);
    if (!prototype) {
      prototype = this.buildBoardCellPrototype(normalized);
      this.cellPrototypes.set(key, prototype);
    }

    const instance = prototype.clone(true);
    instance.name = `LumenwildCell:${normalized.active ? 'active' : 'void'}`;
    instance.userData = { ...prototype.userData, instance: true };
    instance.scale.setScalar(options.scale ?? 1);
    return instance;
  }

  /** Build a chapter-specific tray, rails and corner ornaments around a grid. */
  createBoardFrame(options: BoardFrameOptions): THREE.Group {
    const width = Math.max(1, integer(options.width, 1));
    const height = Math.max(1, integer(options.height, 1));
    const chapter = chapterIndex(options.chapter);
    const cellSize = Math.max(0.4, options.cellSize ?? this.options.cellSize);
    const palette = LUMENWILD_CHAPTER_ART[chapter];
    const frame = this.makeGroup(`LumenwildFrame:${palette.shortName}`, 'board-frame', {
      chapter,
      width,
      height,
      cellSize,
    });
    const boardWidth = width * cellSize;
    const boardDepth = height * cellSize;
    const outerWidth = boardWidth + 0.56 * cellSize;
    const outerDepth = boardDepth + 0.56 * cellSize;

    const lower = this.mesh(
      this.extrudeShape(`frame-base:${outerWidth.toFixed(3)}:${outerDepth.toFixed(3)}`, (shape) => {
        this.roundedRect(shape, outerWidth, outerDepth, 0.17 * cellSize);
      }, 0.24 * cellSize, 0.055 * cellSize),
      this.standard(`frame-base:${chapter}`, palette.primary, {
        roughness: 0.48,
        metalness: 0.18,
      }),
      'frame-base',
    );
    lower.position.y = -0.19 * cellSize;
    frame.add(lower);

    const inset = this.mesh(
      this.extrudeShape(`frame-inset:${outerWidth.toFixed(3)}:${outerDepth.toFixed(3)}`, (shape) => {
        this.roundedRect(shape, outerWidth - 0.18 * cellSize, outerDepth - 0.18 * cellSize, 0.13 * cellSize);
      }, 0.07 * cellSize, 0.028 * cellSize),
      this.standard(`frame-inset:${chapter}`, brighten(palette.primary, 0.035), {
        roughness: 0.62,
        metalness: 0.1,
      }),
      'frame-inset',
    );
    inset.position.y = -0.035 * cellSize;
    frame.add(inset);

    const railMaterial = this.standard(`frame-rail:${chapter}`, palette.secondary, {
      roughness: 0.34,
      metalness: chapter === 2 ? 0.72 : 0.24,
      emissive: palette.glow,
      emissiveIntensity: 0.045,
    });
    const railHeight = 0.12 * cellSize;
    const railY = 0.085 * cellSize;
    const horizontalRail = this.geometry(`frame-rail-h:${boardWidth.toFixed(3)}:${cellSize}`, () => new THREE.BoxGeometry(boardWidth + 0.26 * cellSize, railHeight, 0.08 * cellSize));
    const verticalRail = this.geometry(`frame-rail-v:${boardDepth.toFixed(3)}:${cellSize}`, () => new THREE.BoxGeometry(0.08 * cellSize, railHeight, boardDepth + 0.26 * cellSize));
    for (const z of [-boardDepth / 2 - 0.13 * cellSize, boardDepth / 2 + 0.13 * cellSize]) {
      const rail = this.mesh(horizontalRail, railMaterial, 'frame-rail-horizontal');
      rail.position.set(0, railY, z);
      frame.add(rail);
    }
    for (const x of [-boardWidth / 2 - 0.13 * cellSize, boardWidth / 2 + 0.13 * cellSize]) {
      const rail = this.mesh(verticalRail, railMaterial, 'frame-rail-vertical');
      rail.position.set(x, railY, 0);
      frame.add(rail);
    }

    const cornerGeometry = this.geometry(`frame-corner:${chapter}`, () => new THREE.DodecahedronGeometry(0.15 * cellSize, 0));
    const cornerMaterial = this.standard(`frame-corner:${chapter}`, palette.glow, {
      roughness: 0.26,
      metalness: chapter === 2 ? 0.52 : 0.18,
      emissive: palette.glow,
      emissiveIntensity: 0.15,
    });
    for (const x of [-boardWidth / 2 - 0.19 * cellSize, boardWidth / 2 + 0.19 * cellSize]) {
      for (const z of [-boardDepth / 2 - 0.19 * cellSize, boardDepth / 2 + 0.19 * cellSize]) {
        const corner = this.mesh(cornerGeometry, cornerMaterial, 'frame-corner');
        corner.position.set(x, 0.11 * cellSize, z);
        corner.rotation.set(0.2, (x + z) * 0.5, 0.12);
        frame.add(corner);
      }
    }

    const ornament = this.buildFrameOrnament(chapter, cellSize);
    for (const x of [-boardWidth / 2 - 0.14 * cellSize, boardWidth / 2 + 0.14 * cellSize]) {
      const copy = ornament.clone(true);
      copy.position.set(x, 0.1 * cellSize, 0);
      copy.rotation.y = x < 0 ? Math.PI / 2 : -Math.PI / 2;
      frame.add(copy);
    }

    frame.scale.setScalar(options.scale ?? 1);
    frame.userData.layout = { boardWidth, boardDepth, cellSize };
    return frame;
  }

  /** Place a low-draw decorative chapter layer around a frame or scene. */
  createChapterAccents(chapterNumber: number, options: ChapterAccentOptions = {}): THREE.Group {
    const chapter = chapterIndex(chapterNumber);
    const width = Math.max(2, options.width ?? 8);
    const height = Math.max(2, options.height ?? 8);
    const cellSize = Math.max(0.4, options.cellSize ?? this.options.cellSize);
    const defaultDensity = this.options.quality === 'high' ? 12 : this.options.quality === 'mobile' ? 5 : 8;
    const density = clamp(integer(options.density ?? defaultDensity, defaultDensity), 3, 16);
    const seed = options.seed ?? chapter * 7919 + width * 97 + height * 193;
    const random = seededRandom(seed);
    const accents = this.makeGroup(`LumenwildAccents:${LUMENWILD_CHAPTER_ART[chapter].shortName}`, 'chapter-accents', {
      chapter,
      seed,
      density,
    });
    const spanX = width * cellSize * 0.5 + 0.65 * cellSize;
    const spanZ = height * cellSize * 0.5 + 0.65 * cellSize;

    for (let index = 0; index < density; index += 1) {
      const variant = index % 3;
      const key = `${chapter}:${variant}:${cellSize.toFixed(3)}`;
      let prototype = this.accentPrototypes.get(key);
      if (!prototype) {
        prototype = this.buildAccentPrototype(chapter, variant, cellSize);
        this.accentPrototypes.set(key, prototype);
      }
      const accent = prototype.clone(true);
      const side = index % 4;
      const along = random() * 2 - 1;
      if (side === 0) accent.position.set(along * spanX, 0.02 + random() * 0.18, -spanZ);
      else if (side === 1) accent.position.set(spanX, 0.02 + random() * 0.18, along * spanZ);
      else if (side === 2) accent.position.set(along * spanX, 0.02 + random() * 0.18, spanZ);
      else accent.position.set(-spanX, 0.02 + random() * 0.18, along * spanZ);
      accent.rotation.y = random() * Math.PI * 2;
      const accentScale = (0.74 + random() * 0.38) * (options.scale ?? 1);
      accent.scale.setScalar(accentScale);
      accent.userData.instance = true;
      accents.add(accent);
    }
    return accents;
  }

  /** Naming alias for scene code that calls these an environment layer. */
  createEnvironmentAccents(chapter: number, options: ChapterAccentOptions = {}): THREE.Group {
    return this.createChapterAccents(chapter, options);
  }

  /**
   * Dispose the factory-owned cache.  Call after every instance has been
   * removed from the scene.  Returned groups share these resources by design.
   */
  dispose(): void {
    for (const geometry of this.geometries.values()) geometry.dispose();
    for (const material of this.materials.values()) material.dispose();
    this.geometries.clear();
    this.materials.clear();
    this.piecePrototypes.clear();
    this.blockerPrototypes.clear();
    this.cellPrototypes.clear();
    this.accentPrototypes.clear();
  }

  private buildPiecePrototype(type: PieceType, special: SpecialKind): THREE.Group {
    const style = LUMENWILD_PIECE_ART[type];
    const root = this.makeGroup(`PiecePrototype:${style.name}:${special}`, 'piece', {
      pieceType: type,
      special,
      name: style.name,
      cue: style.icon,
      pattern: style.pattern,
      silhouette: style.silhouette,
      animation: {
        bob: this.options.reducedMotion ? 0 : 0.018,
        sway: this.options.reducedMotion ? 0 : 0.035,
        specialSpin: this.options.reducedMotion ? 0 : special === 'prism' ? 0.22 : 0.08,
      },
    });
    root.add(this.buildPieceBody(type));
    if (this.options.patternCues || this.options.highContrast) root.add(this.buildPieceGlyph(type));
    if (special !== 'none') root.add(this.buildSpecialOverlay(type, special));
    return root;
  }

  private buildPieceBody(type: PieceType): THREE.Group {
    const style = LUMENWILD_PIECE_ART[type];
    const materials = this.pieceMaterials(type);
    const body = this.makeGroup(`PieceBody:${style.name}`, 'piece-body');

    switch (type) {
      case 0: {
        const leafGeometry = this.extrudeShape('piece-cinderleaf-leaf', (shape) => {
          shape.moveTo(0, -0.3);
          shape.quadraticCurveTo(-0.31, -0.11, -0.2, 0.16);
          shape.quadraticCurveTo(-0.1, 0.34, 0.015, 0.4);
          shape.quadraticCurveTo(0.22, 0.26, 0.23, 0.02);
          shape.quadraticCurveTo(0.22, -0.17, 0, -0.3);
        }, 0.13, 0.026);
        const veinGeometry = this.geometry('piece-cinderleaf-vein', () => new THREE.BoxGeometry(0.026, 0.028, 0.28));
        for (let index = 0; index < 3; index += 1) {
          const angle = index * (Math.PI * 2 / 3) - Math.PI / 2;
          const leaf = this.mesh(leafGeometry, materials.body, `cinderleaf-lobed-${index}`);
          leaf.rotation.y = angle;
          leaf.position.set(Math.sin(angle) * 0.025, 0.02, Math.cos(angle) * 0.025);
          leaf.scale.setScalar(0.82);
          body.add(leaf);
          const vein = this.mesh(veinGeometry, materials.dark, `cinderleaf-vein-${index}`);
          vein.rotation.y = angle;
          vein.position.set(Math.sin(angle) * 0.13, 0.096, Math.cos(angle) * 0.13);
          vein.scale.set(0.72, 1, 0.74);
          body.add(vein);
        }
        const seed = this.mesh(this.sphere('piece-cinderleaf-seed', 0.105, 12, 8), materials.light, 'cinderleaf-seed');
        seed.position.y = 0.105;
        body.add(seed);
        break;
      }
      case 1: {
        const capGeometry = this.extrudeShape('piece-mooncap-cap', (shape) => {
          shape.moveTo(-0.35, -0.02);
          shape.quadraticCurveTo(-0.25, -0.23, 0, -0.24);
          shape.quadraticCurveTo(0.27, -0.22, 0.35, -0.01);
          shape.quadraticCurveTo(0.23, 0.2, 0, 0.24);
          shape.quadraticCurveTo(-0.23, 0.2, -0.35, -0.02);
        }, 0.15, 0.035);
        const stemGeometry = this.extrudeShape('piece-mooncap-stem', (shape) => {
          shape.moveTo(-0.12, -0.19);
          shape.quadraticCurveTo(-0.1, -0.04, -0.15, 0.17);
          shape.quadraticCurveTo(0, 0.23, 0.15, 0.17);
          shape.quadraticCurveTo(0.1, -0.04, 0.12, -0.19);
          shape.quadraticCurveTo(0, -0.24, -0.12, -0.19);
        }, 0.12, 0.025);
        const stem = this.mesh(stemGeometry, materials.light, 'mooncap-stem');
        stem.position.y = 0.015;
        stem.scale.set(0.85, 1, 0.8);
        body.add(stem);
        const cap = this.mesh(capGeometry, materials.body, 'mooncap-cap');
        cap.position.y = 0.11;
        body.add(cap);
        const spotGeometry = this.sphere('piece-mooncap-spot', 0.045, 10, 6);
        const spots: readonly [number, number, number][] = [
          [-0.16, 0.19, 0.055],
          [0.02, 0.24, 0.068],
          [0.17, 0.13, 0.052],
          [-0.02, 0.08, 0.06],
        ];
        for (let index = 0; index < spots.length; index += 1) {
          const spot = this.mesh(spotGeometry, materials.light, `mooncap-spot-${index}`);
          spot.position.set(spots[index][0], 0.205, spots[index][1]);
          spot.scale.set(1, 0.52, 1);
          body.add(spot);
        }
        break;
      }
      case 2: {
        const gearGeometry = this.extrudeShape('piece-gearbud-gear', (shape) => {
          const teeth = 24;
          for (let index = 0; index < teeth; index += 1) {
            const angle = index / teeth * Math.PI * 2 - Math.PI / 2;
            const radius = index % 2 === 0 ? 0.34 : 0.255;
            const x = Math.cos(angle) * radius;
            const y = Math.sin(angle) * radius;
            if (index === 0) shape.moveTo(x, y);
            else shape.lineTo(x, y);
          }
          shape.closePath();
        }, 0.14, 0.025);
        const gear = this.mesh(gearGeometry, materials.body, 'gearbud-wheel');
        gear.rotation.y = 0.06;
        gear.position.y = 0.025;
        body.add(gear);
        const hub = this.mesh(this.cylinder('piece-gearbud-hub', 0.14, 0.16, 10), materials.metal, 'gearbud-hub');
        hub.position.y = 0.12;
        body.add(hub);
        const petalGeometry = this.sphere('piece-gearbud-petal', 0.1, 10, 6);
        for (let index = 0; index < 3; index += 1) {
          const angle = index * (Math.PI * 2 / 3) + 0.18;
          const petal = this.mesh(petalGeometry, materials.light, `gearbud-petal-${index}`);
          petal.position.set(Math.cos(angle) * 0.12, 0.14, Math.sin(angle) * 0.12);
          petal.scale.set(1.05, 0.42, 0.68);
          body.add(petal);
        }
        break;
      }
      case 3: {
        const pearGeometry = this.lathe('piece-prismpear-body', [
          [0.02, -0.32],
          [0.15, -0.29],
          [0.25, -0.16],
          [0.29, 0.01],
          [0.24, 0.18],
          [0.13, 0.3],
          [0.02, 0.35],
        ]);
        const pear = this.mesh(pearGeometry, materials.body, 'prism-pear-body');
        pear.rotation.z = -0.035;
        body.add(pear);
        const facetGeometry = this.geometry('piece-prismpear-facet', () => new THREE.CylinderGeometry(0.12, 0.12, 0.035, 6));
        const facet = this.mesh(facetGeometry, materials.light, 'prism-pear-facet');
        facet.position.set(0.02, 0.24, 0.04);
        facet.rotation.y = 0.22;
        body.add(facet);
        const stem = this.mesh(this.cylinder('piece-prismpear-stem', 0.035, 0.1, 7), materials.dark, 'prism-pear-stem');
        stem.position.set(0.01, 0.055, 0.24);
        stem.rotation.z = -0.22;
        body.add(stem);
        const ridgeGeometry = this.torus('piece-prismpear-ridge', 0.215, 0.012, 0.82);
        const ridge = this.mesh(ridgeGeometry, materials.edge, 'prism-pear-ridge');
        ridge.position.y = 0.065;
        ridge.scale.set(1, 1, 0.72);
        body.add(ridge);
        break;
      }
      case 4: {
        const base = this.mesh(this.extrudeShape('piece-coralquill-base', (shape) => {
          shape.moveTo(-0.25, -0.18);
          shape.quadraticCurveTo(-0.04, -0.31, 0.2, -0.18);
          shape.quadraticCurveTo(0.32, 0.02, 0.13, 0.26);
          shape.quadraticCurveTo(-0.08, 0.34, -0.25, 0.16);
          shape.quadraticCurveTo(-0.34, 0, -0.25, -0.18);
        }, 0.12, 0.025), materials.body, 'coralquill-base');
        base.position.y = 0.02;
        body.add(base);
        const paths: readonly THREE.Vector3[][] = [
          [new THREE.Vector3(-0.14, 0.08, -0.08), new THREE.Vector3(-0.11, 0.1, 0.07), new THREE.Vector3(-0.02, 0.11, 0.22), new THREE.Vector3(0.05, 0.11, 0.3)],
          [new THREE.Vector3(0, 0.1, -0.07), new THREE.Vector3(0.08, 0.11, 0.04), new THREE.Vector3(0.16, 0.1, 0.18), new THREE.Vector3(0.2, 0.1, 0.27)],
          [new THREE.Vector3(-0.12, 0.09, -0.02), new THREE.Vector3(-0.23, 0.1, 0.09), new THREE.Vector3(-0.29, 0.1, 0.2)],
        ];
        for (let index = 0; index < paths.length; index += 1) {
          const quill = this.mesh(this.tube(`piece-coralquill-tube-${index}`, paths[index], 0.038), materials.edge, `coralquill-quill-${index}`);
          body.add(quill);
        }
        const barbGeometry = this.extrudeShape('piece-coralquill-barb', (shape) => {
          shape.moveTo(0, 0.1);
          shape.lineTo(0.09, -0.025);
          shape.lineTo(-0.015, -0.02);
          shape.closePath();
        }, 0.075, 0.014);
        const barb = this.mesh(barbGeometry, materials.light, 'coralquill-barbs');
        barb.position.set(0.06, 0.13, 0.17);
        barb.rotation.y = -0.35;
        body.add(barb);
        break;
      }
      case 5: {
        const dropGeometry = this.lathe('piece-inkdrop-body', [
          [0.015, -0.34],
          [0.18, -0.29],
          [0.28, -0.12],
          [0.26, 0.08],
          [0.15, 0.23],
          [0.02, 0.37],
        ]);
        const drop = this.mesh(dropGeometry, materials.body, 'inkdrop-body');
        drop.rotation.z = 0.055;
        body.add(drop);
        const halo = this.mesh(this.torus('piece-inkdrop-halo', 0.2, 0.018, 0.82), materials.edge, 'inkdrop-halo');
        halo.position.y = 0.055;
        halo.scale.set(0.88, 1, 0.72);
        body.add(halo);
        const bubbleGeometry = this.sphere('piece-inkdrop-bubble', 0.055, 10, 6);
        const bubblePositions: readonly [number, number][] = [[-0.13, 0.14], [0.11, 0.2], [0.15, -0.02]];
        for (let index = 0; index < bubblePositions.length; index += 1) {
          const bubble = this.mesh(bubbleGeometry, materials.light, `inkdrop-bubble-${index}`);
          bubble.position.set(bubblePositions[index][0], 0.23, bubblePositions[index][1]);
          bubble.scale.set(1, 0.38, 1);
          body.add(bubble);
        }
        break;
      }
      case 6: {
        const rayGeometry = this.extrudeShape('piece-suncoil-ray', (shape) => {
          shape.moveTo(0, 0.34);
          shape.lineTo(-0.065, 0.16);
          shape.lineTo(0.065, 0.16);
          shape.closePath();
        }, 0.085, 0.016);
        for (let index = 0; index < 8; index += 1) {
          const ray = this.mesh(rayGeometry, materials.light, `suncoil-ray-${index}`);
          ray.rotation.y = index * Math.PI / 4;
          ray.position.y = 0.015;
          body.add(ray);
        }
        const spiralPoints: THREE.Vector3[] = [];
        for (let index = 0; index < 25; index += 1) {
          const t = index / 24;
          const angle = t * Math.PI * 4.7;
          const radius = 0.25 * (1 - t) + 0.035;
          spiralPoints.push(new THREE.Vector3(Math.cos(angle) * radius, 0.12 + t * 0.018, Math.sin(angle) * radius));
        }
        const spiral = this.mesh(this.tube('piece-suncoil-spiral', spiralPoints, 0.032), materials.edge, 'suncoil-spiral');
        body.add(spiral);
        const core = this.mesh(this.sphere('piece-suncoil-core', 0.095, 12, 8), materials.glow, 'suncoil-core');
        core.position.y = 0.14;
        body.add(core);
        break;
      }
      default:
        break;
    }
    return body;
  }

  private buildPieceGlyph(type: PieceType): THREE.Group {
    const style = LUMENWILD_PIECE_ART[type];
    const materials = this.pieceMaterials(type);
    const glyph = this.makeGroup(`PieceGlyph:${style.icon}`, 'piece-glyph', { cue: style.icon, pattern: style.pattern });
    const y = 0.155;
    const barGeometry = this.geometry('glyph-bar', () => new THREE.BoxGeometry(0.027, 0.018, 0.17));
    const dotGeometry = this.sphere('glyph-dot', 0.032, 8, 5);
    switch (type) {
      case 0: {
        for (let index = 0; index < 3; index += 1) {
          const bar = this.mesh(barGeometry, materials.dark, `glyph-leaf-vein-${index}`);
          bar.position.set(0, y + index * 0.003, 0.03);
          bar.rotation.y = (index - 1) * 0.28;
          bar.scale.z = index === 1 ? 1.2 : 0.8;
          glyph.add(bar);
        }
        break;
      }
      case 1: {
        const positions: readonly [number, number][] = [[-0.11, 0.05], [0.01, 0.12], [0.12, -0.02]];
        for (let index = 0; index < positions.length; index += 1) {
          const dot = this.mesh(dotGeometry, materials.dark, `glyph-moon-dot-${index}`);
          dot.position.set(positions[index][0], y + 0.01, positions[index][1]);
          glyph.add(dot);
        }
        break;
      }
      case 2: {
        const hub = this.mesh(this.cylinder('glyph-gear-hub', 0.09, 0.025, 6), materials.dark, 'glyph-gear-hub');
        hub.position.y = y;
        glyph.add(hub);
        break;
      }
      case 3: {
        for (let index = 0; index < 3; index += 1) {
          const bar = this.mesh(barGeometry, materials.dark, `glyph-pear-facet-${index}`);
          bar.position.set((index - 1) * 0.065, y + 0.005, 0.025);
          bar.rotation.y = (index - 1) * 0.4;
          bar.scale.z = 0.75;
          glyph.add(bar);
        }
        break;
      }
      case 4: {
        for (let index = 0; index < 3; index += 1) {
          const barb = this.mesh(barGeometry, materials.dark, `glyph-coral-barb-${index}`);
          barb.position.set((index - 1) * 0.07, y, 0.025 + index * 0.025);
          barb.rotation.y = -0.55 + index * 0.5;
          barb.scale.z = 0.7;
          glyph.add(barb);
        }
        break;
      }
      case 5: {
        const ringGeometry = this.torus('glyph-ink-ring', 0.055, 0.014, 0.8);
        for (let index = 0; index < 2; index += 1) {
          const ring = this.mesh(ringGeometry, materials.light, `glyph-ink-ring-${index}`);
          ring.position.set((index === 0 ? -1 : 1) * 0.075, y, index === 0 ? 0.04 : -0.02);
          ring.scale.setScalar(index === 0 ? 0.8 : 0.63);
          glyph.add(ring);
        }
        break;
      }
      case 6: {
        const ring = this.mesh(this.torus('glyph-sun-ring', 0.09, 0.018, 0.85), materials.dark, 'glyph-sun-ring');
        ring.position.y = y;
        glyph.add(ring);
        const dot = this.mesh(dotGeometry, materials.dark, 'glyph-sun-dot');
        dot.position.y = y + 0.005;
        glyph.add(dot);
        break;
      }
      default:
        break;
    }
    return glyph;
  }

  private buildSpecialOverlay(type: PieceType, special: Exclude<SpecialKind, 'none'>): THREE.Group {
    const style = LUMENWILD_PIECE_ART[type];
    const materials = this.pieceMaterials(type);
    const group = this.makeGroup(`SpecialOverlay:${special}`, 'special-overlay', {
      special,
      cue: LUMENWILD_SPECIAL_ART[special].cue,
      effect: LUMENWILD_SPECIAL_ART[special].effect,
      readable: true,
    });
    const glowMaterial = this.standard(`special-glow:${type}:${special}`, style.accent, {
      roughness: 0.18,
      metalness: 0.08,
      emissive: style.accent,
      emissiveIntensity: 0.72,
    });
    switch (special) {
      case 'beamH': {
        const bar = this.mesh(this.geometry('special-beam-bar-h', () => new THREE.BoxGeometry(0.58, 0.035, 0.06)), glowMaterial, 'special-horizon-bar');
        bar.position.y = 0.17;
        group.add(bar);
        const arrow = this.geometry('special-beam-arrow', () => new THREE.ConeGeometry(0.072, 0.14, 4));
        for (const x of [-0.32, 0.32]) {
          const tip = this.mesh(arrow, materials.light, 'special-horizon-arrow');
          tip.position.set(x, 0.17, 0);
          tip.rotation.z = x < 0 ? -Math.PI / 2 : Math.PI / 2;
          group.add(tip);
        }
        const rail = this.mesh(this.torus('special-beam-ring-h', 0.28, 0.012, 0.84), glowMaterial, 'special-horizon-ring');
        rail.position.y = 0.152;
        rail.scale.set(1.35, 1, 0.42);
        group.add(rail);
        break;
      }
      case 'beamV': {
        const bar = this.mesh(this.geometry('special-beam-bar-v', () => new THREE.BoxGeometry(0.06, 0.035, 0.58)), glowMaterial, 'special-vertical-bar');
        bar.position.y = 0.17;
        group.add(bar);
        const arrow = this.geometry('special-beam-arrow-v', () => new THREE.ConeGeometry(0.072, 0.14, 4));
        for (const z of [-0.32, 0.32]) {
          const tip = this.mesh(arrow, materials.light, 'special-vertical-arrow');
          tip.position.set(0, 0.17, z);
          tip.rotation.x = z < 0 ? Math.PI / 2 : -Math.PI / 2;
          group.add(tip);
        }
        const rail = this.mesh(this.torus('special-beam-ring-v', 0.28, 0.012, 0.84), glowMaterial, 'special-vertical-ring');
        rail.position.y = 0.152;
        rail.scale.set(0.42, 1, 1.35);
        group.add(rail);
        break;
      }
      case 'nova': {
        const ring = this.mesh(this.torus('special-nova-ring', 0.27, 0.026, 0.84), glowMaterial, 'special-nova-ring');
        ring.position.y = 0.17;
        group.add(ring);
        const petalGeometry = this.geometry('special-nova-petal', () => new THREE.OctahedronGeometry(0.075, 0));
        for (let index = 0; index < 4; index += 1) {
          const angle = index * Math.PI / 2 + Math.PI / 4;
          const petal = this.mesh(petalGeometry, materials.light, `special-nova-petal-${index}`);
          petal.position.set(Math.cos(angle) * 0.22, 0.18, Math.sin(angle) * 0.22);
          petal.scale.set(0.8, 0.4, 0.8);
          petal.rotation.y = angle;
          group.add(petal);
        }
        const core = this.mesh(this.sphere('special-nova-core', 0.095, 10, 6), glowMaterial, 'special-nova-core');
        core.position.y = 0.2;
        group.add(core);
        break;
      }
      case 'prism': {
        const prismMaterial = this.physical(`special-prism-glass:${type}`, brighten(style.color, 0.25, 0.08), {
          roughness: 0.12,
          metalness: 0.04,
          emissive: style.accent,
          emissiveIntensity: 0.42,
          transparent: true,
          opacity: 0.84,
          depthWrite: false,
        });
        const prism = this.mesh(this.geometry('special-prism-core', () => new THREE.IcosahedronGeometry(0.19, 1)), prismMaterial, 'special-prism-core');
        prism.position.y = 0.23;
        prism.rotation.set(0.25, 0.35, 0.15);
        group.add(prism);
        const outline = this.lineSegments(this.edges('special-prism-core', prism.geometry), this.line(`special-prism-outline:${type}`, style.accent, 0.82), 'special-prism-outline');
        outline.position.copy(prism.position);
        outline.rotation.copy(prism.rotation);
        group.add(outline);
        const orbit = this.mesh(this.torus('special-prism-orbit', 0.27, 0.012, 0.84), glowMaterial, 'special-prism-orbit');
        orbit.position.y = 0.18;
        orbit.rotation.z = 0.43;
        group.add(orbit);
        const nodeGeometry = this.sphere('special-prism-node', 0.034, 8, 5);
        for (let index = 0; index < 6; index += 1) {
          const angle = index / 6 * Math.PI * 2;
          const node = this.mesh(nodeGeometry, materials.light, `special-prism-node-${index}`);
          node.position.set(Math.cos(angle) * 0.27, 0.19, Math.sin(angle) * 0.27);
          group.add(node);
        }
        break;
      }
      case 'burst': {
        const knot = this.mesh(this.geometry('special-burst-knot', () => new THREE.DodecahedronGeometry(0.2, 0)), glowMaterial, 'special-burst-knot');
        knot.position.y = 0.18;
        knot.rotation.set(0.25, 0.1, 0.2);
        group.add(knot);
        const orbitGeometry = this.torus('special-burst-orbit', 0.24, 0.025, 0.84);
        for (let index = 0; index < 2; index += 1) {
          const orbit = this.mesh(orbitGeometry, materials.light, `special-burst-orbit-${index}`);
          orbit.position.y = 0.18;
          orbit.rotation.z = index === 0 ? 0.52 : -0.52;
          orbit.rotation.x = index === 0 ? 0.18 : -0.18;
          group.add(orbit);
        }
        const spark = this.geometry('special-burst-spark', () => new THREE.OctahedronGeometry(0.055, 0));
        for (let index = 0; index < 4; index += 1) {
          const angle = index * Math.PI / 2 + Math.PI / 4;
          const point = this.mesh(spark, materials.light, `special-burst-spark-${index}`);
          point.position.set(Math.cos(angle) * 0.3, 0.17, Math.sin(angle) * 0.3);
          point.scale.y = 0.5;
          group.add(point);
        }
        break;
      }
      default:
        break;
    }
    return group;
  }

  private buildSelectionHalo(type: PieceType): THREE.Group {
    const style = LUMENWILD_PIECE_ART[type];
    const halo = this.makeGroup('PieceSelectionHalo', 'selection-feedback', { pieceType: type });
    const ring = this.mesh(this.torus(`selection-ring:${type}`, 0.39, 0.014, 0.86), this.standard(`selection-ring:${type}`, style.accent, {
      roughness: 0.16,
      metalness: 0.05,
      emissive: style.accent,
      emissiveIntensity: 0.7,
      transparent: true,
      opacity: 0.9,
    }), 'selection-ring');
    ring.position.y = 0.015;
    ring.scale.set(1, 1, 0.88);
    halo.add(ring);
    const notchGeometry = this.geometry('selection-notch', () => new THREE.OctahedronGeometry(0.045, 0));
    for (let index = 0; index < 4; index += 1) {
      const angle = index * Math.PI / 2 + Math.PI / 4;
      const notch = this.mesh(notchGeometry, this.standard(`selection-notch:${type}`, '#fff8dc', {
        emissive: style.accent,
        emissiveIntensity: 0.46,
        roughness: 0.24,
      }), `selection-notch-${index}`);
      notch.position.set(Math.cos(angle) * 0.4, 0.035, Math.sin(angle) * 0.4);
      halo.add(notch);
    }
    return halo;
  }

  private buildBlockerPrototype(kind: BlockerKind, stage: number, layerHint?: number): THREE.Group {
    const safeStage = clamp(stage, 0, 3);
    const descriptor = LUMENWILD_BLOCKER_ART[kind];
    const root = this.makeGroup(`BlockerPrototype:${descriptor.name}:damage-${safeStage}`, 'blocker', {
      blockerKind: kind,
      damageState: safeStage,
      maxDamageState: 3,
      cue: descriptor.cue,
      layers: layerHint ?? 3 - safeStage,
    });
    if (kind === 'none') return root;

    const wear = safeStage / 3;
    const palette = this.blockerPalette(kind);
    const bodyMaterial = this.standard(`blocker-body:${kind}:${safeStage}`, palette.body, {
      roughness: kind === 'glass' ? 0.12 : 0.42,
      metalness: kind === 'meteor' ? 0.3 : 0.08,
      emissive: kind === 'meteor' ? palette.glow : '#000000',
      emissiveIntensity: kind === 'meteor' ? 0.16 + wear * 0.28 : 0,
      transparent: kind === 'glass',
      opacity: kind === 'glass' ? 0.42 - wear * 0.08 : 1,
      depthWrite: kind !== 'glass',
    });
    const edgeMaterial = this.standard(`blocker-edge:${kind}:${safeStage}`, palette.edge, {
      roughness: 0.28,
      metalness: kind === 'lock' || kind === 'meteor' ? 0.4 : 0.12,
      emissive: kind === 'meteor' ? palette.glow : '#000000',
      emissiveIntensity: kind === 'meteor' ? 0.28 + wear * 0.3 : 0.02,
    });
    const crackMaterial = this.line(`blocker-cracks:${kind}:${safeStage}`, palette.crack, 0.9);

    switch (kind) {
      case 'glass': {
        const paneGeometry = this.extrudeShape(`blocker-glass-pane:${safeStage}`, (shape) => {
          this.roundedRect(shape, 0.78, 0.78, 0.11);
        }, 0.07, 0.025);
        const pane = this.mesh(paneGeometry, bodyMaterial, 'glass-pane');
        pane.position.y = 0.035;
        root.add(pane);
        const outline = this.lineSegments(this.edges(`blocker-glass-pane:${safeStage}`, paneGeometry), crackMaterial, 'glass-outline');
        outline.position.copy(pane.position);
        root.add(outline);
        if (safeStage > 0) {
          const cracks = this.lineSegments(this.crackGeometry(`glass-crack:${safeStage}`, safeStage), crackMaterial, 'glass-cracks');
          cracks.position.y = 0.08;
          root.add(cracks);
        }
        const shine = this.mesh(this.geometry('blocker-glass-shine', () => new THREE.BoxGeometry(0.035, 0.012, 0.48)), this.standard('blocker-glass-shine', '#f4fdff', {
          roughness: 0.1,
          emissive: '#d7fbff',
          emissiveIntensity: 0.35,
          transparent: true,
          opacity: 0.68,
        }), 'glass-shine');
        shine.position.set(-0.2, 0.095, 0.08);
        shine.rotation.y = -0.42;
        root.add(shine);
        break;
      }
      case 'bramble': {
        const vineMaterial = this.standard(`blocker-bramble-vine:${safeStage}`, palette.body, {
          roughness: 0.68,
          metalness: 0.02,
          emissive: palette.glow,
          emissiveIntensity: 0.04,
        });
        const vines: readonly THREE.Vector3[][] = [
          [new THREE.Vector3(-0.34, 0.08, -0.25), new THREE.Vector3(-0.23, 0.1, -0.07), new THREE.Vector3(-0.04, 0.09, -0.17), new THREE.Vector3(0.13, 0.1, -0.3), new THREE.Vector3(0.34, 0.08, -0.12)],
          [new THREE.Vector3(-0.31, 0.1, 0.19), new THREE.Vector3(-0.13, 0.09, 0.32), new THREE.Vector3(0.04, 0.11, 0.16), new THREE.Vector3(0.22, 0.1, 0.3), new THREE.Vector3(0.33, 0.1, 0.16)],
        ];
        const activeVines = safeStage >= 3 ? 1 : 2;
        for (let index = 0; index < activeVines; index += 1) {
          const vine = this.mesh(this.tube(`blocker-bramble-vine-${index}`, vines[index], 0.043), vineMaterial, `bramble-vine-${index}`);
          root.add(vine);
        }
        const thornGeometry = this.geometry('blocker-bramble-thorn', () => new THREE.ConeGeometry(0.052, 0.14, 5));
        const thornPositions: readonly [number, number, number][] = [
          [-0.24, 0.12, -0.12], [0.03, 0.12, -0.18], [0.25, 0.12, -0.2],
          [-0.2, 0.12, 0.2], [0.08, 0.12, 0.21], [0.27, 0.12, 0.17],
        ];
        const thornCount = Math.max(2, 6 - safeStage);
        for (let index = 0; index < thornCount; index += 1) {
          const thorn = this.mesh(thornGeometry, edgeMaterial, `bramble-thorn-${index}`);
          thorn.position.set(thornPositions[index][0], thornPositions[index][1], thornPositions[index][2]);
          thorn.rotation.z = (index % 2 === 0 ? -1 : 1) * (0.32 + index * 0.04);
          root.add(thorn);
        }
        if (safeStage > 0) root.add(this.lineSegments(this.crackGeometry(`bramble-crack:${safeStage}`, safeStage), crackMaterial, 'bramble-damage')); 
        break;
      }
      case 'wax': {
        const wax = this.mesh(this.geometry(`blocker-wax-body:${safeStage}`, () => new THREE.DodecahedronGeometry(0.33, 1)), bodyMaterial, 'wax-seal');
        wax.position.y = 0.17;
        wax.scale.set(1, 0.52 - wear * 0.04, 0.94);
        wax.rotation.set(0.12, 0.18, -0.1);
        root.add(wax);
        const seal = this.mesh(this.cylinder(`blocker-wax-seal:${safeStage}`, 0.2, 0.05, 10), edgeMaterial, 'wax-stamp');
        seal.position.y = 0.35 - wear * 0.04;
        seal.rotation.y = 0.18;
        root.add(seal);
        const dripGeometry = this.geometry('blocker-wax-drip', () => new THREE.SphereGeometry(0.075, 8, 6));
        for (let index = 0; index < 3 - Math.min(2, safeStage); index += 1) {
          const drip = this.mesh(dripGeometry, bodyMaterial, `wax-drip-${index}`);
          drip.position.set(-0.21 + index * 0.19, 0.08, 0.24 - (index % 2) * 0.1);
          drip.scale.set(0.62, 1.4, 0.72);
          root.add(drip);
        }
        if (safeStage > 0) {
          const cracks = this.lineSegments(this.crackGeometry(`wax-crack:${safeStage}`, safeStage), crackMaterial, 'wax-cracks');
          cracks.position.y = 0.38;
          root.add(cracks);
        }
        break;
      }
      case 'lock': {
        const body = this.mesh(this.extrudeShape('blocker-lock-body', (shape) => {
          this.roundedRect(shape, 0.52, 0.42, 0.08);
        }, 0.13, 0.025), bodyMaterial, 'lock-body');
        body.position.y = 0.11;
        root.add(body);
        const shacklePoints = [
          new THREE.Vector3(-0.14, 0.13, 0.06),
          new THREE.Vector3(-0.14, 0.15, 0.24),
          new THREE.Vector3(0, 0.17, 0.34),
          new THREE.Vector3(0.14, 0.15, 0.24),
          new THREE.Vector3(0.14, 0.13, 0.06),
        ];
        const shackle = this.mesh(this.tube('blocker-lock-shackle', shacklePoints, 0.043), edgeMaterial, 'lock-shackle');
        root.add(shackle);
        const keyhole = this.mesh(this.cylinder('blocker-lock-keyhole', 0.06, 0.028, 8), crackMaterial, 'lock-keyhole');
        keyhole.position.set(0, 0.2, -0.04);
        root.add(keyhole);
        const keyStem = this.mesh(this.geometry('blocker-lock-key-stem', () => new THREE.BoxGeometry(0.025, 0.03, 0.13)), crackMaterial, 'lock-key-stem');
        keyStem.position.set(0, 0.21, -0.13);
        root.add(keyStem);
        if (safeStage > 0) root.add(this.lineSegments(this.crackGeometry(`lock-crack:${safeStage}`, safeStage), crackMaterial, 'lock-damage'));
        break;
      }
      case 'meteor': {
        const rock = this.mesh(this.geometry(`blocker-meteor-rock:${safeStage}`, () => new THREE.IcosahedronGeometry(0.34, 1)), bodyMaterial, 'meteor-rock');
        rock.position.y = 0.18;
        rock.scale.set(1, 0.58, 0.94);
        rock.rotation.set(0.18, 0.25, -0.18);
        root.add(rock);
        const ember = this.mesh(this.sphere(`blocker-meteor-ember:${safeStage}`, 0.11 + wear * 0.025, 10, 6), edgeMaterial, 'meteor-ember');
        ember.position.set(0.08, 0.35, 0.03);
        ember.scale.y = 0.45;
        root.add(ember);
        if (safeStage > 0) {
          const cracks = this.lineSegments(this.crackGeometry(`meteor-crack:${safeStage}`, safeStage), crackMaterial, 'meteor-fissures');
          cracks.position.y = 0.37;
          root.add(cracks);
        }
        break;
      }
      default:
        break;
    }
    root.userData.visualWear = wear;
    return root;
  }

  private buildBoardCellPrototype(options: NormalizedCellOptions): THREE.Group {
    const palette = LUMENWILD_CHAPTER_ART[chapterIndex(options.chapter)];
    const root = this.makeGroup(`CellPrototype:${options.active ? 'active' : 'void'}`, 'board-cell', {
      chapter: chapterIndex(options.chapter),
      active: options.active,
      locked: options.locked,
      hazard: options.hazard,
      portal: options.portal,
      conveyor: options.conveyor,
      spawn: options.spawn,
      exit: options.exit,
    });
    const cellBase = this.mesh(this.extrudeShape(`cell-base:${options.active ? 'active' : 'void'}`, (shape) => {
      this.roundedRect(shape, 0.9, 0.9, 0.12);
    }, 0.1, 0.035), this.standard(`cell-base:${options.chapter}:${options.active ? 1 : 0}`, options.active ? brighten(palette.primary, 0.09) : '#151e34', {
      roughness: 0.58,
      metalness: 0.12,
    }), 'cell-base');
    cellBase.position.y = -0.01;
    root.add(cellBase);
    const inlay = this.mesh(this.extrudeShape(`cell-inlay:${options.active ? 'active' : 'void'}`, (shape) => {
      this.roundedRect(shape, 0.72, 0.72, 0.09);
    }, 0.035, 0.015), this.standard(`cell-inlay:${options.chapter}:${options.active ? 1 : 0}`, options.active ? brighten(palette.primary, 0.16) : '#0d162b', {
      roughness: 0.72,
      metalness: 0.04,
      emissive: options.active ? palette.background : '#000000',
      emissiveIntensity: options.active ? 0.12 : 0,
    }), 'cell-inlay');
    inlay.position.y = 0.055;
    root.add(inlay);
    const rim = this.lineSegments(this.edges(`cell-inlay:${options.active ? 'active' : 'void'}`, inlay.geometry), this.line(`cell-rim:${options.chapter}`, palette.secondary, 0.42), 'cell-rim');
    rim.position.copy(inlay.position);
    root.add(rim);

    if (!options.active) {
      const voidRing = this.mesh(this.torus(`cell-void-ring:${options.chapter}`, 0.28, 0.018, 0.84), this.standard(`cell-void-ring:${options.chapter}`, '#293553', {
        roughness: 0.52,
        emissive: '#17223b',
        emissiveIntensity: 0.25,
      }), 'cell-void-ring');
      voidRing.position.y = 0.08;
      voidRing.scale.set(1, 1, 0.82);
      root.add(voidRing);
    }
    if (options.locked) root.add(this.buildCellLockCue(options.chapter));
    if (options.hazard > 0) root.add(this.buildHazardCue(options.chapter, options.hazard));
    if (options.portal) root.add(this.buildPortalCue(options.chapter, options.portal));
    if (options.conveyor) root.add(this.buildConveyorCue(options.chapter, options.conveyor));
    if (options.spawn) root.add(this.buildEntryCue(options.chapter, 'spawn'));
    if (options.exit) root.add(this.buildEntryCue(options.chapter, 'exit'));
    return root;
  }

  private buildCellLockCue(chapter: number): THREE.Group {
    const group = this.makeGroup('CellLockCue', 'cell-mechanic-cue', { mechanic: 'locked' });
    const palette = LUMENWILD_CHAPTER_ART[chapterIndex(chapter)];
    const body = this.mesh(this.extrudeShape('cell-lock-body', (shape) => {
      this.roundedRect(shape, 0.26, 0.2, 0.04);
    }, 0.06, 0.015), this.standard(`cell-lock-body:${chapter}`, palette.secondary, {
      roughness: 0.34,
      metalness: 0.35,
    }), 'cell-lock-body');
    body.position.y = 0.12;
    group.add(body);
    const shackle = this.mesh(this.torus(`cell-lock-shackle:${chapter}`, 0.095, 0.018, Math.PI), this.standard(`cell-lock-shackle:${chapter}`, palette.glow, {
      roughness: 0.28,
      metalness: 0.44,
    }), 'cell-lock-shackle');
    shackle.position.set(0, 0.13, 0.095);
    shackle.rotation.z = Math.PI;
    group.add(shackle);
    return group;
  }

  private buildHazardCue(chapter: number, hazard: number): THREE.Group {
    const group = this.makeGroup('CellHazardCue', 'cell-mechanic-cue', { mechanic: 'hazard', level: hazard });
    const palette = LUMENWILD_CHAPTER_ART[chapterIndex(chapter)];
    const material = this.standard(`cell-hazard:${chapter}:${hazard}`, '#f17372', {
      roughness: 0.3,
      emissive: '#ff6e66',
      emissiveIntensity: 0.22 + hazard * 0.08,
      transparent: true,
      opacity: 0.5 + hazard * 0.12,
    });
    const ring = this.mesh(this.torus(`cell-hazard-ring:${chapter}:${hazard}`, 0.28, 0.02, 0.84), material, 'hazard-ring');
    ring.position.y = 0.09;
    ring.scale.set(1, 1, 0.76);
    group.add(ring);
    const warning = this.mesh(this.geometry(`cell-hazard-warning:${hazard}`, () => new THREE.ConeGeometry(0.105, 0.035, 3)), this.standard(`cell-hazard-warning:${chapter}`, palette.glow, {
      roughness: 0.28,
      emissive: '#ff8b73',
      emissiveIntensity: 0.34,
    }), 'hazard-warning');
    warning.position.y = 0.11;
    warning.rotation.y = Math.PI / 6;
    group.add(warning);
    return group;
  }

  private buildPortalCue(chapter: number, portalId: string): THREE.Group {
    const group = this.makeGroup('CellPortalCue', 'cell-mechanic-cue', { mechanic: 'portal', portal: portalId });
    const hue = (Math.abs(hashString(portalId)) % 360) / 360;
    const color = new THREE.Color().setHSL(hue, 0.65, 0.68);
    const portalMaterial = this.standard(`cell-portal:${chapter}:${portalId}`, color, {
      roughness: 0.18,
      metalness: 0.16,
      emissive: color,
      emissiveIntensity: 0.46,
    });
    const ring = this.mesh(this.torus(`cell-portal-ring:${chapter}:${portalId}`, 0.25, 0.035, 0.9), portalMaterial, 'portal-ring');
    ring.position.y = 0.1;
    ring.scale.set(1, 1, 0.76);
    group.add(ring);
    const ringInner = this.mesh(this.torus(`cell-portal-inner:${chapter}:${portalId}`, 0.14, 0.012, 0.9), portalMaterial, 'portal-inner-ring');
    ringInner.position.y = 0.105;
    ringInner.scale.set(1, 1, 0.76);
    group.add(ringInner);
    const core = this.mesh(this.sphere(`cell-portal-core:${chapter}:${portalId}`, 0.055, 8, 5), portalMaterial, 'portal-core');
    core.position.y = 0.11;
    core.scale.y = 0.34;
    group.add(core);
    return group;
  }

  private buildConveyorCue(chapter: number, direction: ConveyorDirection): THREE.Group {
    const group = this.makeGroup('CellConveyorCue', 'cell-mechanic-cue', { mechanic: 'conveyor', direction });
    const palette = LUMENWILD_CHAPTER_ART[chapterIndex(chapter)];
    const material = this.standard(`cell-conveyor:${chapter}`, palette.glow, {
      roughness: 0.27,
      metalness: 0.25,
      emissive: palette.glow,
      emissiveIntensity: 0.26,
    });
    const bar = this.mesh(this.geometry(`cell-conveyor-bar:${direction}`, () => new THREE.BoxGeometry(0.36, 0.018, 0.028)), material, 'conveyor-bar');
    bar.position.y = 0.11;
    if (direction === 'up' || direction === 'down') bar.rotation.y = Math.PI / 2;
    group.add(bar);
    const arrow = this.geometry(`cell-conveyor-arrow:${direction}`, () => new THREE.ConeGeometry(0.065, 0.13, 4));
    const tip = this.mesh(arrow, material, 'conveyor-arrow');
    tip.position.y = 0.11;
    if (direction === 'left') {
      tip.position.x = -0.23;
      tip.rotation.z = -Math.PI / 2;
    } else if (direction === 'right') {
      tip.position.x = 0.23;
      tip.rotation.z = Math.PI / 2;
    } else if (direction === 'up') {
      tip.position.z = 0.23;
      tip.rotation.x = -Math.PI / 2;
    } else {
      tip.position.z = -0.23;
      tip.rotation.x = Math.PI / 2;
    }
    group.add(tip);
    return group;
  }

  private buildEntryCue(chapter: number, type: 'spawn' | 'exit'): THREE.Group {
    const group = this.makeGroup(`Cell${type === 'spawn' ? 'Spawn' : 'Exit'}Cue`, 'cell-mechanic-cue', { mechanic: type });
    const palette = LUMENWILD_CHAPTER_ART[chapterIndex(chapter)];
    const material = this.standard(`cell-entry:${chapter}:${type}`, type === 'spawn' ? palette.glow : palette.secondary, {
      roughness: 0.3,
      metalness: 0.16,
      emissive: palette.glow,
      emissiveIntensity: 0.22,
    });
    const gate = this.mesh(this.torus(`cell-entry-gate:${chapter}:${type}`, 0.23, 0.018, Math.PI), material, 'entry-gate');
    gate.position.y = 0.1;
    gate.rotation.z = type === 'spawn' ? 0 : Math.PI;
    group.add(gate);
    const arrow = this.mesh(this.geometry(`cell-entry-arrow:${type}`, () => new THREE.ConeGeometry(0.06, 0.14, 4)), material, 'entry-arrow');
    arrow.position.set(0, 0.11, type === 'spawn' ? 0.22 : -0.22);
    arrow.rotation.x = type === 'spawn' ? -Math.PI / 2 : Math.PI / 2;
    group.add(arrow);
    return group;
  }

  private buildFrameOrnament(chapter: ChapterId, scale: number): THREE.Group {
    const group = this.makeGroup('FrameOrnament', 'frame-ornament', { chapter });
    const palette = LUMENWILD_CHAPTER_ART[chapter];
    const material = this.standard(`frame-ornament:${chapter}`, palette.secondary, {
      roughness: 0.3,
      metalness: chapter === 2 ? 0.68 : 0.18,
      emissive: palette.glow,
      emissiveIntensity: 0.12,
    });
    if (chapter === 0) {
      const leaf = this.extrudeShape('frame-ornament-leaf', (shape) => {
        shape.moveTo(0, -0.11);
        shape.quadraticCurveTo(-0.12, -0.01, -0.03, 0.13);
        shape.quadraticCurveTo(0.11, 0.08, 0, -0.11);
      }, 0.055, 0.012);
      for (let index = 0; index < 3; index += 1) {
        const mesh = this.mesh(leaf, material, `frame-leaf-${index}`);
          mesh.position.set(0, 0, (index - 1) * 0.11 * scale);
          mesh.scale.setScalar(scale);
        mesh.rotation.y = index * 0.55 - 0.55;
        group.add(mesh);
      }
    } else if (chapter === 1) {
      const ring = this.mesh(this.torus('frame-ornament-moonring', 0.17 * scale, 0.018 * scale, 0.85), material, 'frame-moonring');
      ring.rotation.z = 0.4;
      group.add(ring);
      const dot = this.mesh(this.sphere('frame-ornament-moon-dot', 0.045 * scale, 8, 5), material, 'frame-moon-dot');
      dot.position.set(0.1 * scale, 0.025, 0.08 * scale);
      group.add(dot);
    } else if (chapter === 2) {
      const gear = this.extrudeShape('frame-ornament-gear', (shape) => {
        const teeth = 16;
        for (let index = 0; index < teeth; index += 1) {
          const angle = index / teeth * Math.PI * 2;
          const radius = index % 2 === 0 ? 0.18 : 0.14;
          const x = Math.cos(angle) * radius;
          const y = Math.sin(angle) * radius;
          if (index === 0) shape.moveTo(x, y);
          else shape.lineTo(x, y);
        }
        shape.closePath();
      }, 0.065, 0.012);
      const mesh = this.mesh(gear, material, 'frame-gear');
      mesh.rotation.y = 0.2;
      mesh.scale.setScalar(scale);
      group.add(mesh);
      const hub = this.mesh(this.cylinder('frame-ornament-hub', 0.055 * scale, 0.03 * scale, 8), material, 'frame-gear-hub');
      hub.position.y = 0.05 * scale;
      group.add(hub);
    } else if (chapter === 3) {
      const coral = this.mesh(this.tube('frame-ornament-coral', [
        new THREE.Vector3(-0.17 * scale, 0.02, 0),
        new THREE.Vector3(-0.05 * scale, 0.04, 0.08 * scale),
        new THREE.Vector3(0.03 * scale, 0.03, 0.02 * scale),
        new THREE.Vector3(0.15 * scale, 0.04, 0.13 * scale),
      ], 0.025 * scale), material, 'frame-coral');
      group.add(coral);
    } else {
      const coil = this.mesh(this.tube('frame-ornament-coil', [
        new THREE.Vector3(-0.22 * scale, 0.02, 0),
        new THREE.Vector3(-0.12 * scale, 0.05, 0.08 * scale),
        new THREE.Vector3(0, 0.04, 0),
        new THREE.Vector3(0.14 * scale, 0.06, -0.07 * scale),
        new THREE.Vector3(0.22 * scale, 0.04, 0.02 * scale),
      ], 0.022 * scale), material, 'frame-suncoil');
      group.add(coil);
    }
    return group;
  }

  private buildAccentPrototype(chapter: ChapterId, variant: number, scale: number): THREE.Group {
    const group = this.makeGroup(`AccentPrototype:${chapter}:${variant}`, 'environment-accent', { chapter, variant });
    const palette = LUMENWILD_CHAPTER_ART[chapter];
    const glow = this.standard(`accent-glow:${chapter}`, palette.glow, {
      roughness: 0.2,
      metalness: 0.08,
      emissive: palette.glow,
      emissiveIntensity: 0.44,
    });
    if (chapter === 0) {
      const leaf = this.extrudeShape('accent-emberleaf', (shape) => {
        shape.moveTo(0, -0.3);
        shape.quadraticCurveTo(-0.2, -0.08, -0.11, 0.22);
        shape.quadraticCurveTo(0.02, 0.34, 0.16, 0.25);
        shape.quadraticCurveTo(0.19, -0.02, 0, -0.3);
      }, 0.08, 0.018);
      for (let index = 0; index < 2 + variant; index += 1) {
        const mesh = this.mesh(leaf, this.standard(`accent-leaf:${chapter}:${variant}`, variant === 1 ? palette.glow : palette.secondary, {
          roughness: 0.48,
          emissive: palette.secondary,
          emissiveIntensity: 0.12,
        }), `emberleaf-${index}`);
        mesh.position.set((index - 1) * 0.12 * scale, 0.04 + index * 0.02, (index % 2) * 0.11 * scale);
        mesh.rotation.y = index * 0.8 - 0.4;
        mesh.scale.setScalar(0.46 * scale);
        group.add(mesh);
      }
      const ember = this.mesh(this.sphere(`accent-ember:${variant}`, 0.045 * scale, 8, 5), glow, 'ember-glow');
      ember.position.y = 0.22 * scale;
      group.add(ember);
    } else if (chapter === 1) {
      const cap = this.buildPieceBody(1);
      cap.scale.setScalar(0.52 * scale);
      cap.position.y = 0.02;
      group.add(cap);
      const ring = this.mesh(this.torus('accent-moonring', 0.23 * scale, 0.014 * scale, 0.84), glow, 'moon-ring');
      ring.position.y = 0.02;
      ring.scale.set(1, 1, 0.64);
      group.add(ring);
    } else if (chapter === 2) {
      const gear = this.buildPieceBody(2);
      gear.scale.setScalar(0.48 * scale);
      gear.rotation.y = variant * 0.5;
      group.add(gear);
      const pollen = this.mesh(this.sphere(`accent-pollen:${variant}`, 0.035 * scale, 8, 5), glow, 'brass-pollen');
      pollen.position.set(0.25 * scale, 0.2 * scale, 0.15 * scale);
      group.add(pollen);
    } else if (chapter === 3) {
      const coral = this.buildPieceBody(4);
      coral.scale.setScalar(0.5 * scale);
      coral.rotation.y = variant * 0.7;
      group.add(coral);
      const pearl = this.mesh(this.sphere(`accent-pearl:${variant}`, 0.05 * scale, 8, 5), glow, 'reef-pearl');
      pearl.position.set(0.22 * scale, 0.22 * scale, -0.12 * scale);
      group.add(pearl);
    } else {
      const sun = this.buildPieceBody(6);
      sun.scale.setScalar(0.47 * scale);
      sun.rotation.y = variant * 0.8;
      group.add(sun);
      const star = this.mesh(this.geometry(`accent-star:${variant}:${scale.toFixed(3)}`, () => new THREE.OctahedronGeometry(0.05 * scale, 0)), glow, 'astral-star');
      star.position.set(-0.25 * scale, 0.2 * scale, 0.18 * scale);
      group.add(star);
    }
    return group;
  }

  private pieceMaterials(type: PieceType): PieceMaterials {
    const style = LUMENWILD_PIECE_ART[type];
    const color = this.options.highContrast ? brighten(style.color, -0.04, 0.02) : style.color;
    return {
      body: this.standard(`piece-body:${type}`, color, {
        roughness: 0.29,
        metalness: 0.06,
        emissive: brighten(color, -0.2),
        emissiveIntensity: 0.08,
      }),
      edge: this.standard(`piece-edge:${type}`, brighten(style.color, 0.1, 0.015), {
        roughness: 0.21,
        metalness: 0.16,
        emissive: style.accent,
        emissiveIntensity: 0.07,
      }),
      dark: this.standard(`piece-dark:${type}`, this.options.highContrast ? '#10182b' : '#27324b', {
        roughness: 0.34,
        metalness: 0.03,
      }),
      light: this.standard(`piece-light:${type}`, style.accent, {
        roughness: 0.2,
        metalness: 0.04,
        emissive: style.accent,
        emissiveIntensity: 0.12,
      }),
      glow: this.standard(`piece-glow:${type}`, style.accent, {
        roughness: 0.16,
        metalness: 0.08,
        emissive: style.accent,
        emissiveIntensity: 0.6,
      }),
      metal: this.standard(`piece-metal:${type}`, brighten(style.color, 0.18, -0.04), {
        roughness: 0.25,
        metalness: 0.56,
        emissive: style.accent,
        emissiveIntensity: 0.04,
      }),
    };
  }

  private blockerPalette(kind: BlockerKind): { body: string; edge: string; glow: string; crack: string } {
    switch (kind) {
      case 'glass': return { body: '#a8e4ee', edge: '#e8ffff', glow: '#baf7ff', crack: '#f4ffff' };
      case 'bramble': return { body: '#4e7a60', edge: '#b4d378', glow: '#b5ebad', crack: '#e4ffd4' };
      case 'wax': return { body: '#d48b55', edge: '#ffe0a0', glow: '#ffd48b', crack: '#fff0b9' };
      case 'lock': return { body: '#7262a9', edge: '#d6c6ff', glow: '#d6c6ff', crack: '#f8efff' };
      case 'meteor': return { body: '#6b5369', edge: '#e27a65', glow: '#ffae67', crack: '#ffe0a0' };
      default: return { body: '#26324d', edge: '#aebbd0', glow: '#d8e4ff', crack: '#ffffff' };
    }
  }

  private roundedRect(shape: THREE.Shape, width: number, height: number, radius: number): void {
    const r = Math.min(Math.abs(radius), Math.min(width, height) * 0.48);
    const left = -width / 2;
    const right = width / 2;
    const bottom = -height / 2;
    const top = height / 2;
    shape.moveTo(left + r, bottom);
    shape.lineTo(right - r, bottom);
    shape.quadraticCurveTo(right, bottom, right, bottom + r);
    shape.lineTo(right, top - r);
    shape.quadraticCurveTo(right, top, right - r, top);
    shape.lineTo(left + r, top);
    shape.quadraticCurveTo(left, top, left, top - r);
    shape.lineTo(left, bottom + r);
    shape.quadraticCurveTo(left, bottom, left + r, bottom);
    shape.closePath();
  }

  private extrudeShape(key: string, builder: ShapeBuilder, depth: number, bevel: number): THREE.ExtrudeGeometry {
    return this.geometry(`extrude:${key}:${depth.toFixed(3)}:${bevel.toFixed(3)}`, () => {
      const shape = new THREE.Shape();
      builder(shape);
      const geometry = new THREE.ExtrudeGeometry(shape, {
        depth,
        bevelEnabled: true,
        bevelSegments: this.options.quality === 'mobile' ? 1 : 2,
        bevelThickness: bevel,
        bevelSize: bevel,
        curveSegments: this.detail,
      });
      geometry.translate(0, 0, -depth / 2);
      geometry.rotateX(Math.PI / 2);
      geometry.computeVertexNormals();
      return geometry;
    });
  }

  private lathe(key: string, points: readonly [number, number][]): THREE.LatheGeometry {
    return this.geometry(`lathe:${key}`, () => {
      const geometry = new THREE.LatheGeometry(points.map(([radius, y]) => new THREE.Vector2(radius, y)), this.radialSegments);
      geometry.rotateX(Math.PI / 2);
      geometry.computeVertexNormals();
      return geometry;
    });
  }

  private sphere(key: string, radius: number, widthSegments = this.detail, heightSegments = Math.max(5, Math.floor(this.detail * 0.65))): THREE.SphereGeometry {
    return this.geometry(`sphere:${key}:${radius.toFixed(3)}`, () => new THREE.SphereGeometry(radius, widthSegments, heightSegments));
  }

  private cylinder(key: string, radius: number, depth: number, segments = this.radialSegments): THREE.CylinderGeometry {
    return this.geometry(`cylinder:${key}:${radius.toFixed(3)}:${depth.toFixed(3)}`, () => new THREE.CylinderGeometry(radius, radius * 0.94, depth, segments));
  }

  private torus(key: string, radius: number, tube: number, arc = Math.PI * 2): THREE.TorusGeometry {
    return this.geometry(`torus:${key}:${radius.toFixed(3)}:${tube.toFixed(3)}:${arc.toFixed(3)}`, () => {
      const geometry = new THREE.TorusGeometry(radius, tube, Math.max(5, Math.floor(this.detail * 0.55)), this.detail, arc);
      geometry.rotateX(Math.PI / 2);
      geometry.computeVertexNormals();
      return geometry;
    });
  }

  private tube(key: string, points: readonly THREE.Vector3[], radius: number): THREE.TubeGeometry {
    return this.geometry(`tube:${key}:${radius.toFixed(3)}`, () => {
      const curve = new THREE.CatmullRomCurve3(points.map((point) => point.clone()), false, 'centripetal', 0.5);
      return new THREE.TubeGeometry(curve, this.options.quality === 'mobile' ? 8 : 12, radius, Math.max(5, Math.floor(this.detail * 0.45)), false);
    });
  }

  private crackGeometry(key: string, stage: number): THREE.BufferGeometry {
    const paths: readonly THREE.Vector3[][] = [
      [new THREE.Vector3(-0.03, 0.095, 0.29), new THREE.Vector3(-0.13, 0.095, 0.12), new THREE.Vector3(-0.08, 0.095, -0.03), new THREE.Vector3(-0.22, 0.095, -0.2)],
      [new THREE.Vector3(0.02, 0.095, 0.16), new THREE.Vector3(0.13, 0.095, 0.08), new THREE.Vector3(0.09, 0.095, -0.08), new THREE.Vector3(0.23, 0.095, -0.23)],
      [new THREE.Vector3(-0.08, 0.095, 0.03), new THREE.Vector3(0.02, 0.095, -0.05), new THREE.Vector3(0.15, 0.095, -0.02)],
    ];
    const points: THREE.Vector3[] = [];
    const count = clamp(stage, 1, paths.length);
    for (let index = 0; index < count; index += 1) points.push(...paths[index]);
    return this.geometry(`cracks:${key}`, () => {
      const geometry = new THREE.BufferGeometry().setFromPoints(points);
      return geometry;
    });
  }

  private edges(key: string, geometry: THREE.BufferGeometry): THREE.EdgesGeometry {
    return this.geometry(`edges:${key}`, () => new THREE.EdgesGeometry(geometry, 22));
  }

  private makeGroup(name: string, artKind: string, userData: Record<string, unknown> = {}): THREE.Group {
    const group = new THREE.Group();
    group.name = name;
    group.userData = { artKind, ...userData };
    return group;
  }

  private mesh<T extends THREE.BufferGeometry>(geometry: T, material: THREE.Material, name: string): THREE.Mesh<T, THREE.Material> {
    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = name;
    mesh.castShadow = this.options.shadows;
    mesh.receiveShadow = true;
    mesh.userData.artRole = name;
    return mesh;
  }

  private lineSegments(geometry: THREE.BufferGeometry, material: THREE.Material, name: string): THREE.LineSegments {
    const line = new THREE.LineSegments(geometry, material);
    line.name = name;
    line.userData.artRole = name;
    return line;
  }

  private geometry<T extends THREE.BufferGeometry>(key: string, builder: () => T): T {
    const cached = this.geometries.get(key);
    if (cached) return cached as T;
    const geometry = builder();
    this.geometries.set(key, geometry);
    return geometry;
  }

  private standard(key: string, color: THREE.ColorRepresentation, options: MaterialOptions = {}): THREE.MeshStandardMaterial {
    const cacheKey = `standard:${key}:${this.options.highContrast ? 'contrast' : 'normal'}`;
    const cached = this.materials.get(cacheKey);
    if (cached) return cached as THREE.MeshStandardMaterial;
    const material = new THREE.MeshStandardMaterial({
      color,
      roughness: options.roughness ?? 0.45,
      metalness: options.metalness ?? 0.08,
      emissive: options.emissive ?? '#000000',
      emissiveIntensity: options.emissiveIntensity ?? 0,
      transparent: options.transparent ?? false,
      opacity: options.opacity ?? 1,
      side: options.side ?? THREE.FrontSide,
      depthWrite: options.depthWrite ?? true,
      flatShading: options.flatShading ?? false,
    });
    this.materials.set(cacheKey, material);
    return material;
  }

  private physical(key: string, color: THREE.ColorRepresentation, options: MaterialOptions = {}): THREE.MeshPhysicalMaterial {
    const cacheKey = `physical:${key}:${this.options.highContrast ? 'contrast' : 'normal'}`;
    const cached = this.materials.get(cacheKey);
    if (cached) return cached as THREE.MeshPhysicalMaterial;
    const material = new THREE.MeshPhysicalMaterial({
      color,
      roughness: options.roughness ?? 0.18,
      metalness: options.metalness ?? 0.05,
      clearcoat: 0.8,
      clearcoatRoughness: 0.16,
      emissive: options.emissive ?? '#000000',
      emissiveIntensity: options.emissiveIntensity ?? 0,
      transparent: options.transparent ?? false,
      opacity: options.opacity ?? 1,
      side: options.side ?? THREE.FrontSide,
      depthWrite: options.depthWrite ?? true,
    });
    this.materials.set(cacheKey, material);
    return material;
  }

  private line(key: string, color: THREE.ColorRepresentation, opacity = 1): THREE.LineBasicMaterial {
    const cacheKey = `line:${key}:${this.options.highContrast ? 'contrast' : 'normal'}`;
    const cached = this.materials.get(cacheKey);
    if (cached) return cached as THREE.LineBasicMaterial;
    const material = new THREE.LineBasicMaterial({ color, transparent: opacity < 1, opacity, depthWrite: false });
    this.materials.set(cacheKey, material);
    return material;
  }
}

export function createArtFactory(options: ArtFactoryOptions = {}): ArtFactory {
  return new ArtFactory(options);
}

export default ArtFactory;
